/**
 * Profile Generator — pure 2D canvas drawing (no React, no Konva/Fabric).
 *
 * renderScene() draws the layer stack (bottom → top) in LOGICAL canvas units
 * (0..width, 0..height). The caller sets up any zoom/DPR scaling on the context
 * before calling, so the same function serves both on-screen display and clean
 * export (offscreen canvas at native size).
 *
 * Every layer is drawn through a centre-translate + rotate transform so
 * rotation and flipping compose correctly.
 */

// Linear-gradient endpoints across a w×h box centred at (0,0).
// angle: 0 = top→bottom, 90 = left→right (per spec).
const gradientFor = (ctx, angle, w, h, from, to) => {
  const rad = (angle * Math.PI) / 180;
  const dx = Math.sin(rad), dy = Math.cos(rad);
  const g = ctx.createLinearGradient(
    -dx * w / 2, -dy * h / 2,
    dx * w / 2, dy * h / 2,
  );
  g.addColorStop(0, from);
  g.addColorStop(1, to);
  return g;
};

const drawFillLayer = (ctx, layer) => {
  const { width: w, height: h } = layer;
  if (layer.fillType === 'gradient') {
    ctx.fillStyle = gradientFor(ctx, layer.gradientAngle || 0, w, h,
      layer.gradientFrom || '#ffffff', layer.gradientTo || '#000000');
  } else {
    ctx.fillStyle = layer.fill || '#ffffff';
  }
  ctx.fillRect(-w / 2, -h / 2, w, h);
};

const drawImageLayer = (ctx, layer, img) => {
  if (!img) return;
  const { width: w, height: h } = layer;
  const a = layer.adjustments || {};

  ctx.filter = `brightness(${a.brightness ?? 100}%) contrast(${a.contrast ?? 100}%) ` +
    `saturate(${a.saturation ?? 100}%) blur(${a.blur ?? 0}px)`;
  if (a.blendMode && a.blendMode !== 'source-over') ctx.globalCompositeOperation = a.blendMode;
  if (a.shadowEnabled) {
    ctx.shadowColor = a.shadowColor || '#00000055';
    ctx.shadowBlur = a.shadowBlur || 0;
    ctx.shadowOffsetX = a.shadowOffsetX || 0;
    ctx.shadowOffsetY = a.shadowOffsetY || 0;
  }
  const sx = a.flipH ? -1 : 1;
  const sy = a.flipV ? -1 : 1;
  if (sx !== 1 || sy !== 1) ctx.scale(sx, sy);

  // Crop: normalised source rect (whole image when disabled).
  const cw = a.cropEnabled ? (a.cropW || 1) : 1;
  const ch = a.cropEnabled ? (a.cropH || 1) : 1;
  const cox = a.cropEnabled ? (a.cropX || 0) : 0;
  const coy = a.cropEnabled ? (a.cropY || 0) : 0;
  ctx.drawImage(
    img,
    cox * img.naturalWidth, coy * img.naturalHeight,
    cw * img.naturalWidth, ch * img.naturalHeight,
    -w / 2, -h / 2, w, h,
  );
};

const drawNameLayer = (ctx, layer) => {
  const { width: w, height: h } = layer;
  ctx.filter = 'none';
  ctx.fillStyle = layer.fontColor || '#1a1a1a';
  ctx.font = `${layer.fontWeight || '600'} ${layer.fontSize || 24}px ${layer.fontFamily || "'Inter', sans-serif"}`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = layer.textAlign || 'center';
  const tx = layer.textAlign === 'left' ? -w / 2 : layer.textAlign === 'right' ? w / 2 : 0;
  const lines = String(layer.text ?? '').split('\n');
  const lh = (layer.fontSize || 24) * 1.2;
  const startY = -((lines.length - 1) * lh) / 2;
  lines.forEach((line, i) => ctx.fillText(line, tx, startY + i * lh));
};

const drawLayer = (ctx, layer, imageCache) => {
  if (!layer.visible) return;
  ctx.save();
  ctx.globalAlpha = layer.opacity ?? 1;
  // Centre-translate + rotate so all transforms compose around the box centre.
  ctx.translate(layer.x + layer.width / 2, layer.y + layer.height / 2);
  if (layer.rotation) ctx.rotate((layer.rotation * Math.PI) / 180);

  if (layer.type === 'background' || layer.type === 'foreground') drawFillLayer(ctx, layer);
  else if (layer.type === 'image') drawImageLayer(ctx, layer, imageCache?.[layer.src]);
  else if (layer.type === 'name') drawNameLayer(ctx, layer);

  ctx.restore();
  // Reset context bits drawImage/filters may have left set.
  ctx.filter = 'none';
  ctx.globalCompositeOperation = 'source-over';
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
};

export const renderScene = (ctx, { layers, canvasConfig }, imageCache) => {
  const { width, height } = canvasConfig;
  ctx.clearRect(0, 0, width, height);
  for (const layer of layers) drawLayer(ctx, layer, imageCache);
};

// Render the scene to a fresh offscreen canvas at native size for export.
export const renderToCanvas = ({ layers, canvasConfig }, imageCache) => {
  const c = document.createElement('canvas');
  c.width = canvasConfig.width;
  c.height = canvasConfig.height;
  const ctx = c.getContext('2d');
  renderScene(ctx, { layers, canvasConfig }, imageCache);
  return c;
};

export const exportDataURL = (scene, imageCache, type = 'png', quality = 0.92, transparent = false) => {
  const c = renderToCanvas(scene, imageCache);
  if (type === 'jpg' || type === 'jpeg') {
    // JPG has no alpha — flatten onto white unless a bg layer already filled it.
    if (!transparent) {
      const ctx = c.getContext('2d');
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, c.width, c.height);
    }
    return c.toDataURL('image/jpeg', quality);
  }
  return c.toDataURL('image/png');
};

// Small thumbnail data URL (for list/template cards).
export const makeThumbnail = (scene, imageCache, maxW = 320) => {
  const src = renderToCanvas(scene, imageCache);
  const scale = Math.min(1, maxW / src.width);
  const c = document.createElement('canvas');
  c.width = Math.round(src.width * scale);
  c.height = Math.round(src.height * scale);
  const ctx = c.getContext('2d');
  ctx.drawImage(src, 0, 0, c.width, c.height);
  return c.toDataURL('image/png');
};
